import React from "react";
import {useSelector} from "react-redux";

import {
  FormGrid,
  FormSlide,
  FormCard,
  SelectField,
  TextField,
} from "@reside/ui";

import {userTypes, visibleRolesFor} from "../../../utils/constants";
import {readableRole} from "../../../atoms/list-table/helpers";
import {OrganizationSelect} from "./OrganizationSelect";
import {FacilitiesTags} from "./FacilitiesTags";
import {useFormikContext} from "formik";
import {CreateStaffUserDto, UserDto} from "../../../utils/types/user";
import {StyledScrollableErrorArea} from "./styles";
import {OrganizationModel} from "../../../utils/types/organization";
import {RoleNameEnum} from "../../../utils/types/common";
import {AppState, select} from "../../../store";

type Props = Readonly<{
  user?: UserDto;
  userOrganizationHasSsoConfig: boolean;
  organizations: readonly OrganizationModel[];
  deleteUserButton?: React.ReactNode;
  resetPasswordButton?: React.ReactNode;
  resendActivationEmailButton?: React.ReactNode;
}>;

export const UserFormBodyRenderer = ({
  user,
  userOrganizationHasSsoConfig,
  organizations,
  deleteUserButton = null,
  resetPasswordButton = null,
  resendActivationEmailButton = null,
}: Props): JSX.Element => {
  const {values} = useFormikContext<CreateStaffUserDto>();

  // Use the logged-in admin's role to determine visible roles, not the edited user's role
  const adminUser = useSelector((state: AppState) =>
    select.adminSession.user(state),
  );
  const roles = visibleRolesFor(
    (adminUser?.role?.name ?? user?.role?.name) as RoleNameEnum,
  );

  return (
    <StyledScrollableErrorArea>
      <FormGrid>
        <FormSlide>
          <FormCard title="User Details">
            <TextField
              label={"First Name"}
              name="firstName"
              disabled={userOrganizationHasSsoConfig}
            />
            <TextField
              label={"Last Name"}
              name="lastName"
              disabled={userOrganizationHasSsoConfig}
            />
            <SelectField
              label={"Role"}
              name="roleId"
              options={roles}
              getOptionLabel={role => readableRole(role)}
              getOptionValue={role => userTypes[role]}
              disabled={userOrganizationHasSsoConfig}
            />
            <TextField
              label={"Email Address"}
              name="email"
              type="email"
              disabled={userOrganizationHasSsoConfig}
            />
            <TextField
              label={"Phone"}
              name="phone"
              format="us-phone"
              disabled={userOrganizationHasSsoConfig}
            />
            {!userOrganizationHasSsoConfig && resetPasswordButton}
            {!userOrganizationHasSsoConfig && resendActivationEmailButton}
          </FormCard>

          {
            // Don't show organization select if user that is being edited or created is reside admin
            values.roleId !== userTypes.RESIDE_ADMIN && (
              <OrganizationSelect
                organizations={organizations}
                disabled={userOrganizationHasSsoConfig}
              />
            )
          }

          {
            // Don't show facilities select if the user that is being edited or created is reside admin or organization admin or organization IT admin
            ![
              userTypes.ORGANIZATION_IT_ADMIN,
              userTypes.ORGANIZATION_ADMIN,
              userTypes.RESIDE_ADMIN,
            ].includes(values.roleId) && (
              <FacilitiesTags
                user={user}
                disabled={userOrganizationHasSsoConfig}
              />
            )
          }
          {!userOrganizationHasSsoConfig && deleteUserButton}
        </FormSlide>
      </FormGrid>
    </StyledScrollableErrorArea>
  );
};
